import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  canChangeAudience,
  canCreate,
  canDelete,
  canEdit,
  childCollectionsOf,
  getCollection,
  newId,
  parentPointer,
  type AccessFields,
  type Actor,
  type Audience,
  type CollectionDef,
} from '@app/shared';
import { db, type LocalRecord } from '../db.ts';
import { useFamily } from '../session.tsx';
import { enqueue } from '../sync/outbox.ts';
import { syncEngine } from '../sync/engine.ts';

/**
 * Feature modules read and write records only through this module. Writes
 * are optimistic: the local replica updates immediately and a mutation is
 * queued for the sync engine, so everything works the same offline.
 */

function requireDef(collection: string): CollectionDef {
  const def = getCollection(collection);
  if (!def) throw new Error(`Unknown collection ${collection}`);
  return def;
}

function dataKeys(def: CollectionDef): string[] {
  return Object.keys(def.schema.shape);
}

async function descendants(def: CollectionDef, id: string): Promise<{ collection: string; record: LocalRecord }[]> {
  const out: { collection: string; record: LocalRecord }[] = [];
  for (const child of childCollectionsOf(def.name)) {
    const p = child.parent!;
    const rows =
      p.kind === 'fixed'
        ? await db.records(child.name).where(p.field).equals(id).toArray()
        : await db.records(child.name).where('[targetCollection+targetId]').equals([def.name, id]).toArray();
    for (const r of rows) {
      out.push({ collection: child.name, record: r });
      out.push(...(await descendants(child, r.id)));
    }
  }
  return out;
}

export interface Undo {
  undo(): Promise<void>;
}

export interface Repo {
  create(collection: string, data: Record<string, unknown>, opts?: { id?: string; audience?: Audience; sharedWith?: string[] }): Promise<string>;
  update(collection: string, id: string, patch: Record<string, unknown>): Promise<void>;
  remove(collection: string, id: string): Promise<Undo>;
  restoreSnapshot(collection: string, snapshot: LocalRecord): Promise<void>;
}

export function createRepo(familyId: string, actor: Actor): Repo {
  return {
    async create(collection, data, opts = {}) {
      const def = requireDef(collection);
      const input = { ...data };
      for (const f of def.actorFields ?? []) input[f] = actor.userId;
      const parsed = def.schema.parse(input) as Record<string, unknown>;
      const id = opts.id ?? newId();
      let access: Pick<LocalRecord, 'ownerId' | 'audience' | 'sharedWith'>;
      if (def.parent) {
        const ptr = parentPointer(def, parsed);
        const parent = ptr ? await db.records(ptr.collection).get(ptr.id) : undefined;
        access = parent
          ? { ownerId: parent.ownerId, audience: parent.audience, sharedWith: parent.sharedWith }
          : { ownerId: actor.userId, audience: def.defaultAudience, sharedWith: [] };
      } else {
        const audience = opts.audience ?? def.defaultAudience;
        access = {
          ownerId: actor.userId,
          audience,
          sharedWith: audience === 'selected' ? [...new Set(opts.sharedWith ?? [])].filter((u) => u !== actor.userId) : [],
        };
      }
      const now = new Date().toISOString();
      const record: LocalRecord = {
        id,
        familyId,
        ...access,
        createdBy: actor.userId,
        updatedBy: actor.userId,
        createdAt: now,
        updatedAt: now,
        rev: 0,
        ...parsed,
      };
      await db.transaction('rw', db.records(collection), db.outbox, async () => {
        await db.records(collection).put(record);
        await enqueue({
          familyId,
          collection,
          recordId: id,
          op: 'upsert',
          patch: { ...parsed, ...(def.parent ? {} : { audience: access.audience, sharedWith: access.sharedWith }) },
          baseRev: 0,
        });
      });
      syncEngine.schedule();
      return id;
    },

    async update(collection, id, patch) {
      const def = requireDef(collection);
      const table = db.records(collection);
      const current = await table.get(id);
      if (!current) throw new Error('This item is no longer available.');
      const shape = def.schema.shape as Record<string, { parse(v: unknown): unknown }>;
      const clean: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(patch)) {
        if (k === 'audience' || k === 'sharedWith') clean[k] = v;
        else if (shape[k]) clean[k] = shape[k].parse(v);
      }
      if (clean.audience !== undefined && clean.audience !== 'selected') clean.sharedWith = [];
      const next: LocalRecord = { ...current, ...clean, updatedAt: new Date().toISOString(), updatedBy: actor.userId };
      const data = Object.fromEntries(dataKeys(def).map((k) => [k, next[k]]));
      def.schema.parse(data);
      const audienceChanged = 'audience' in clean || 'sharedWith' in clean;
      const children = audienceChanged && !def.parent ? await descendants(def, id) : [];
      await db.transaction('rw', db.tables, async () => {
        await table.put(next);
        for (const c of children) {
          await db.records(c.collection).put({ ...c.record, audience: next.audience, sharedWith: next.sharedWith, ownerId: next.ownerId });
        }
        await enqueue({ familyId, collection, recordId: id, op: 'upsert', patch: clean, baseRev: current.rev });
      });
      syncEngine.schedule();
    },

    async remove(collection, id) {
      const def = requireDef(collection);
      const root = await db.records(collection).get(id);
      if (!root) return { undo: async () => undefined };
      const snapshots = [{ collection, record: root }, ...(await descendants(def, id))];
      await db.transaction('rw', db.tables, async () => {
        for (const s of snapshots) await db.records(s.collection).delete(s.record.id);
        await enqueue({ familyId, collection, recordId: id, op: 'delete', baseRev: root.rev });
      });
      syncEngine.schedule();
      return {
        undo: async () => {
          await db.transaction('rw', db.tables, async () => {
            const queuedDelete = (await db.outbox.where('[collection+recordId]').equals([collection, id]).toArray()).find(
              (e) => e.op === 'delete' && e.state === 'queued',
            );
            if (queuedDelete) await db.outbox.delete(queuedDelete.seq!);
            for (const s of snapshots) await db.records(s.collection).put(s.record);
            if (queuedDelete) return;
            if (root.rev === 0) {
              // Never reached the server: queue the creation again.
              for (const s of snapshots) {
                const d = requireDef(s.collection);
                const patch = Object.fromEntries(dataKeys(d).map((k) => [k, s.record[k]]));
                if (!d.parent) Object.assign(patch, { audience: s.record.audience, sharedWith: s.record.sharedWith });
                await enqueue({ familyId, collection: s.collection, recordId: s.record.id, op: 'upsert', patch, baseRev: 0 });
              }
            } else {
              await enqueue({ familyId, collection, recordId: id, op: 'restore', baseRev: root.rev });
            }
          });
          syncEngine.schedule();
        },
      };
    },

    async restoreSnapshot(collection, snapshot) {
      await db.transaction('rw', db.tables, async () => {
        await db.records(collection).put(snapshot);
        await enqueue({ familyId, collection, recordId: snapshot.id, op: 'restore', baseRev: snapshot.rev });
      });
      syncEngine.schedule();
    },
  };
}

export function useRepo(): Repo {
  const { familyId, actor } = useFamily();
  const { userId, role } = actor;
  return useMemo(() => createRepo(familyId, { userId, role }), [familyId, userId, role]);
}

/** Live list of a collection's records in the active family. */
export function useRecords<T = LocalRecord>(
  collection: string,
  options: { index?: string; equals?: unknown; filter?: (r: T) => boolean; deps?: unknown[] } = {},
): T[] | undefined {
  const { familyId } = useFamily();
  const { index, equals, filter, deps = [] } = options;
  return useLiveQuery(async () => {
    const table = db.records(collection);
    const rows = index
      ? await table
          .where(index)
          .equals(equals as string)
          .toArray()
      : await table.where('familyId').equals(familyId).toArray();
    const own = rows.filter((r) => r.familyId === familyId) as unknown as T[];
    return filter ? own.filter(filter) : own;
  }, [familyId, collection, index, equals, ...deps]);
}

export function useRecord<T = LocalRecord>(collection: string, id: string | null | undefined): T | null | undefined {
  return useLiveQuery(async () => (id ? (((await db.records(collection).get(id)) as T | undefined) ?? null) : null), [collection, id]);
}

/** Permission checks bound to the current person, for showing/hiding actions. */
export function usePermissions() {
  const { actor } = useFamily();
  return useMemo(
    () => ({
      actor,
      canEdit: (collection: string, rec: AccessFields) => canEdit(actor, requireDef(collection), rec),
      canDelete: (collection: string, rec: AccessFields) => canDelete(actor, requireDef(collection), rec),
      canChangeAudience: (collection: string, rec: AccessFields) => canChangeAudience(actor, requireDef(collection), rec),
      canCreate: (collection: string, parent?: { collection: string; record: AccessFields }, data: Record<string, unknown> = {}) =>
        canCreate(
          actor,
          requireDef(collection),
          data,
          parent ? { def: requireDef(parent.collection), record: parent.record as AccessFields & Record<string, unknown> } : undefined,
        ),
    }),
    [actor],
  );
}
